import { parseStyleString, responsiveVarName, stringifyStyleObject } from '@/plugins/tiptap-v3/functionality/responsive/responsive';
import { getColStyleDeclaration } from './colStyle';
const TABLE_COL_WIDTH_MARKER = '-table-col-width-';
const DEFAULT_TABLE_BREAKPOINTS = ['lg', 'md', 'sm'];
function getBreakpointFromDOM(domElement) {
    let cursor = domElement || null;
    while (cursor) {
        const breakpointClass = Array.from(cursor.classList || []).find(cls => cls.startsWith('vp-') && cls.length > 3);
        if (breakpointClass) {
            return breakpointClass.slice(3);
        }
        cursor = cursor.parentElement;
    }
    return null;
}
export function getBreakpoint(editor, domElement) {
    var _a, _b;
    return getBreakpointFromDOM(domElement) || ((_b = (_a = editor === null || editor === void 0 ? void 0 : editor.storage) === null || _a === void 0 ? void 0 : _a.responsiveEngine) === null || _b === void 0 ? void 0 : _b.breakpoint) || 'lg';
}
function isEditableProseMirror(domElement) {
    if (!domElement)
        return false;
    const host = domElement.closest('.ProseMirror');
    if (!host)
        return false;
    return host.getAttribute('contenteditable') === 'true';
}
function getConfiguredBreakpoints(editor) {
    var _a, _b, _c;
    const responsiveEngine = (_b = (_a = editor === null || editor === void 0 ? void 0 : editor.extensionManager) === null || _a === void 0 ? void 0 : _a.extensions) === null || _b === void 0 ? void 0 : _b.find((ext) => ext.name === 'responsiveEngine');
    const bps = (_c = responsiveEngine === null || responsiveEngine === void 0 ? void 0 : responsiveEngine.options) === null || _c === void 0 ? void 0 : _c.breakpoints;
    const out = [];
    [...(Array.isArray(bps) ? bps : []), ...DEFAULT_TABLE_BREAKPOINTS].forEach(bp => {
        if (typeof bp !== 'string')
            return;
        const normalized = bp.trim();
        if (!normalized || out.includes(normalized))
            return;
        out.push(normalized);
    });
    return out;
}
function getExistingBreakpointsForColumn(styleObj, column, prefix, cssPropColWidth) {
    const marker = `--${prefix}-${cssPropColWidth}-`;
    const suffix = `-${column}`;
    const out = [];
    Object.keys(styleObj).forEach(key => {
        if (!key.startsWith(marker) || !key.endsWith(suffix))
            return;
        const breakpoint = key.slice(marker.length, key.length - suffix.length).trim();
        if (!breakpoint || out.includes(breakpoint))
            return;
        out.push(breakpoint);
    });
    return out;
}
function getResponsiveColWidthVarName(column, breakpoint, prefix = 'vp', cssPropColWidth = 'table-col-width') {
    return `${responsiveVarName(cssPropColWidth, breakpoint, prefix)}-${column}`;
}
export function setResponsiveColWidthValue(styleString, column, value, opts) {
    const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(opts === null || opts === void 0 ? void 0 : opts.editor, opts === null || opts === void 0 ? void 0 : opts.domElement);
    const prefix = (opts === null || opts === void 0 ? void 0 : opts.prefix) || 'vp';
    const cssPropColWidth = (opts === null || opts === void 0 ? void 0 : opts.cssPropColWidth) || 'table-col-width';
    const varName = getResponsiveColWidthVarName(column, bp, prefix, cssPropColWidth);
    const styleObj = parseStyleString(styleString || '');
    // Save all breakpoints and only update current breakpoint value.
    const breakpoints = [
        bp,
        ...getConfiguredBreakpoints(opts === null || opts === void 0 ? void 0 : opts.editor),
        ...getExistingBreakpointsForColumn(styleObj, column, prefix, cssPropColWidth),
    ].filter((item, index, list) => !!item && list.indexOf(item) === index);
    const seedValue = breakpoints
        .map(item => styleObj[getResponsiveColWidthVarName(column, item, prefix, cssPropColWidth)])
        .find(item => typeof item === 'string' && item.trim()) || value;
    breakpoints.forEach(item => {
        const key = getResponsiveColWidthVarName(column, item, prefix, cssPropColWidth);
        if (!styleObj[key]) {
            styleObj[key] = seedValue;
        }
    });
    styleObj[varName] = value;
    return stringifyStyleObject(styleObj);
}
export function unsetResponsiveColWidthValue(styleString, column, opts) {
    const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(opts === null || opts === void 0 ? void 0 : opts.editor, opts === null || opts === void 0 ? void 0 : opts.domElement);
    const varName = getResponsiveColWidthVarName(column, bp, (opts === null || opts === void 0 ? void 0 : opts.prefix) || 'vp', (opts === null || opts === void 0 ? void 0 : opts.cssPropColWidth) || 'table-col-width');
    const styleObj = parseStyleString(styleString || '');
    delete styleObj[varName];
    return stringifyStyleObject(styleObj);
}
export function getResponsiveColWidthValue(styleString, column, opts) {
    const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(opts === null || opts === void 0 ? void 0 : opts.editor, opts === null || opts === void 0 ? void 0 : opts.domElement);
    const varName = getResponsiveColWidthVarName(column, bp, (opts === null || opts === void 0 ? void 0 : opts.prefix) || 'vp', (opts === null || opts === void 0 ? void 0 : opts.cssPropColWidth) || 'table-col-width');
    const styleObj = parseStyleString(styleString || '');
    const value = styleObj[varName];
    return typeof value === 'string' && value.trim() ? value : null;
}
export function getResponsiveWidthsByColumn(style) {
    const styleObj = parseStyleString(style || '');
    const out = new Map();
    Object.entries(styleObj).forEach(([key, value]) => {
        if (!key.startsWith('--'))
            return;
        if (!key.includes(TABLE_COL_WIDTH_MARKER))
            return;
        if (!value || !String(value).trim())
            return;
        const lastDash = key.lastIndexOf('-');
        if (lastDash === -1)
            return;
        const colIndex = parseInt(key.slice(lastDash + 1), 10);
        if (!Number.isFinite(colIndex) || colIndex < 0)
            return;
        const markerIdx = key.indexOf(TABLE_COL_WIDTH_MARKER);
        if (markerIdx === -1)
            return;
        const bpStart = markerIdx + TABLE_COL_WIDTH_MARKER.length;
        const breakpoint = key.slice(bpStart, lastDash);
        if (!breakpoint)
            return;
        const current = out.get(colIndex) || {};
        current[breakpoint] = value;
        out.set(colIndex, current);
    });
    return out;
}
export function buildColStyleObject(cellMinWidth, width, responsiveWidths, opts) {
    const hasResponsiveWidths = !!responsiveWidths && Object.keys(responsiveWidths).length > 0;
    const hasFixedWidth = typeof width === 'number' && Number.isFinite(width) && width > 0;
    const fixedWidth = hasFixedWidth ? `${Math.max(width, cellMinWidth)}px` : '';
    // Preserve existing non-responsive behavior when no explicit width exists.
    if (!hasResponsiveWidths && !hasFixedWidth) {
        const [propertyKey, propertyValue] = getColStyleDeclaration(cellMinWidth, width);
        return { [propertyKey]: propertyValue };
    }
    const fallback = fixedWidth || 'auto';
    const currentBreakpoint = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(opts === null || opts === void 0 ? void 0 : opts.editor, opts === null || opts === void 0 ? void 0 : opts.domElement);
    const responsiveCurrentValue = ((responsiveWidths === null || responsiveWidths === void 0 ? void 0 : responsiveWidths[currentBreakpoint]) && String(responsiveWidths[currentBreakpoint]).trim())
        ? responsiveWidths[currentBreakpoint]
        : '';
    // During drag resize, ProseMirror `colwidth` is the source of truth for current breakpoint.
    // If stale responsive vars win here, width can differ after mouseup.
    const currentValue = fixedWidth || responsiveCurrentValue || fallback;
    const editable = isEditableProseMirror((opts === null || opts === void 0 ? void 0 : opts.domElement) || null);
    const widthValue = editable && hasFixedWidth
        ? fixedWidth
        : `var(--table-col-width-current, ${fallback})`;
    const styleObj = {
        width: widthValue,
        'min-width': `${cellMinWidth}px`,
        [`--table-col-width-${currentBreakpoint}`]: currentValue,
    };
    Object.entries(responsiveWidths || {}).forEach(([breakpoint, value]) => {
        if (breakpoint === currentBreakpoint)
            return;
        styleObj[`--table-col-width-${breakpoint}`] = value;
    });
    return styleObj;
}
export function buildColStyleString(cellMinWidth, width, responsiveWidths, opts) {
    return stringifyStyleObject(buildColStyleObject(cellMinWidth, width, responsiveWidths, opts));
}
